import { Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullhorn,
  faClockRotateLeft,
  faRankingStar,
  faMapLocationDot,
  faMobileScreenButton,
  faRoute,
} from "@fortawesome/free-solid-svg-icons";
import { HoverCard, HoverIcon } from "../components/HoverMotion";

const deliveryItems = [
  {
    icon: faMobileScreenButton,
    title: "GPS Tracker Deployment",
    text: "Tracker allocation and handover plan for participants, teams, vehicles, or critical assets.",
  },
  {
    icon: faMapLocationDot,
    title: "Live Tracking Page",
    text: "A map-based monitoring page for race control, crew access, or public spectator visibility.",
  },
  {
    icon: faRoute,
    title: "Route and Checkpoint Context",
    text: "Course data, checkpoints, and operational markers prepared before race day.",
  },
  {
    icon: faBullhorn,
    title: "Race-Day Support",
    text: "Operational support for setup, monitoring readiness, and tracker handling during deployment.",
  },
  {
    icon: faClockRotateLeft,
    title: "Playback History",
    text: "Recorded movement for incident review, participant support, and event evaluation.",
  },
  {
    icon: faRankingStar,
    title: "Live Leaderboard",
    text: "Real-time standings ranked by route progress, with per aid-station arrival order and split times.",
  },
];

// Portfolio: 3 organizer besar utama (logo). Jika logo belum tersedia,
// biarkan `logo` null → tampil sebagai nama teks agar tidak broken.
const mainPartners = [
  {
    name: "Trail of the Kings by UTMB",
    handle: "@trailofthekingsbyutmb",
    logo: "/logos/totk.png",
    href: "https://www.instagram.com/trailofthekingsbyutmb",
  },
  {
    name: "Dieng Caldera Race",
    handle: "@diengcalderarace",
    logo: "/logos/dieng.png",
    href: "https://www.instagram.com/diengcalderarace",
  },
  {
    name: "Cameron Ultra (CULTRA)",
    handle: "@cameronultra",
    logo: "/logos/cultra.avif",
    href: "https://www.instagram.com/cameronultra",
  },
];

// Organizer lain — tampil sebagai teks biasa di bawah logo utama.
const otherPartners = [
  "JAVA580",
  "IRACE Indonesia",
  "Indomaret Run",
  "Indonesia Master Trail",
];

const IconBox = ({ icon }) => (
  <HoverIcon className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-icon border bg-slate-100 border-slate-200 text-slate-700">
    <FontAwesomeIcon icon={icon} className="w-5 h-5" />
  </HoverIcon>
);

export const SectionAboutTracking = () => {
  return (
    <>
      <section id="about" className="w-full py-16 lg:py-24 px-4 bg-slate-50 border-t border-slate-200">
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight mb-3">
              Tracking Package
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              The service is delivered as a working event operation, not only as device rental.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deliveryItems.map((item) => (
              <HoverCard key={item.title} className="bg-white p-4 flex items-start gap-4 rounded-card border border-slate-200">
                <IconBox icon={item.icon} />
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1 text-slate-900">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </HoverCard>
            ))}
          </div>
        </div>
      </section>

      {/* See It Live — replay history TOTK dalam preview browser Mac */}
      <section className="w-full py-16 lg:py-24 px-4 bg-white border-t border-slate-200">
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight mb-3">
              See It Live
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              A real event replay — every participant on the course map, played back from start to finish.
            </p>
          </div>

          <div className="rounded-card border border-slate-200 overflow-hidden bg-white shadow-lg">
            {/* Browser-style top bar (macOS) */}
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-400" />
                <span className="w-3 h-3 rounded-full bg-yellow-400" />
                <span className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 mx-2 px-3 py-1 bg-white rounded-md border border-slate-200 text-xs text-slate-500 truncate">
                tracking.vionixasia.com/map/totk/history
              </div>
              <a
                href="https://tracking.vionixasia.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-slate-600 hover:text-slate-900 whitespace-nowrap"
              >
                Open ↗
              </a>
            </div>
            <img
              src="/totk-replay.png"
              alt="Live tracking replay — Trail of the Kings 2026"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="w-full py-16 px-4 bg-slate-50 border-t border-slate-200">
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight mb-3">
              Trusted by Organizers
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Supporting endurance and outdoor teams that need clearer visibility from start to finish.
            </p>
          </div>

          {/* Logo organizer besar utama */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-16 gap-y-10 mb-12">
            {mainPartners.map((partner) => (
              <a
                key={partner.name}
                href={partner.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center text-center"
                aria-label={partner.name}
              >
                <div className="h-16 flex items-center justify-center mb-3">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-16 w-auto max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm font-bold text-slate-900 leading-tight">{partner.name}</p>
                <p className="text-xs text-slate-500">
                  {partner.handle}
                </p>
              </a>
            ))}
          </div>

          {/* Organizer lain */}
          <div className="border-t border-slate-200 pt-8 text-center">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
              And trusted by more event organizers
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3">
              {otherPartners.map((name, i) => (
                <Fragment key={name}>
                  {i > 0 && (
                    <span className="text-slate-300" aria-hidden="true">
                      •
                    </span>
                  )}
                  <span className="text-sm font-semibold text-slate-600">
                    {name}
                  </span>
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

    </>
  );
};
